<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  type PropType,
  ref,
  SetupContext,
  useAttrs,
  watch,
} from "vue";
import { ControlSize, DesignVersion, InputType } from "@/models/types";

defineOptions({ name: "BcInput", inheritAttrs: false });

const attrs: SetupContext["attrs"] = useAttrs();

/** Specifies two-way binding for the input */
const modelValue = defineModel<string | number | null | undefined>({
  default: undefined,
});

const props = defineProps({
  /** Defines the input's style */
  design: {
    type: String as PropType<DesignVersion>,
    default: "6",
    validator: (value: any) => DesignVersion.isDesignVersion(value),
  },
  /** Disables input element */
  disabled: { type: Boolean, default: false },
  /** Specifies custom validation for the input */
  customValidation: {
    type: Function as PropType<(value?: string | number | null) => boolean>,
  },
  /** Expands the input to the width of its parent container */
  fullWidth: { type: Boolean, default: false },
  /** Specifies custom validation messages */
  errors: {
    type: Map as PropType<Map<string, string>>,
    default: () => new Map<string, string>(),
    validator: (value: any) => value instanceof Map,
  },
  /**
   * Specifies a label for the input.
   * The label is bound to the input.
   */
  label: { type: [String, Number, Boolean], default: undefined },
  /** Specifies wether two-way binding should sync on change event instead of input */
  lazy: { type: Boolean, default: false },
  /** Specifies wether a reveal icon should be shown for the password input */
  passwordToggle: { type: Boolean, default: false },
  /** Expands the input to the width of the placeholder */
  placeholderWidth: { type: Boolean, default: false },
  /** Specifies that input is read-only */
  readonly: { type: Boolean, default: false },
  /** Specifies pre-defined sizes for the input */
  size: {
    type: String as PropType<ControlSize>,
    validator: (value: any) => ControlSize.isControlSize(value),
  },
  /** Specifies the type for the input */
  type: {
    type: String as PropType<InputType>,
    default: "text",
    validator: (value: any) => InputType.isInputType(value),
  },
});

const emit = defineEmits<{
  (e: "blur", payload: string | number | null | undefined): void;
  (e: "change", payload: string | number | null | undefined): void;
  (e: "focus"): void;
  (e: "input", payload: string | number | null | undefined): void;
  (e: "update:model-value", payload: string | number | null | undefined): void;
}>();

const dataType = ref<InputType>(props.type);
const dataValue = ref<string | number | null | undefined>(null);
const errorKeys = ref<string[]>([]);
const isPasswordVisible = ref(false);
const validity = ref(true);

const inputRef = ref<HTMLInputElement | null>(null);

const pak = computed<string>(() => {
  return "bc-controls-d" + props.design;
});

const computedValue = computed({
  get() {
    return dataValue.value;
  },
  set(value: string | number | null | undefined) {
    dataValue.value =
      dataType.value === "number" &&
      typeof value === "string" &&
      !Number.isNaN(Number.parseFloat(value))
        ? Number.parseFloat(value)
        : value ?? null;
  },
});
const rootClass = computed(() => (attrs.class ? (attrs.class as string).split(" ") : []));
const rootStyle = computed(() => attrs.style as any);
const inheritedAttrs = computed(() => {
  const { class: string, style: any, ...otherProps } = attrs;
  const inheritedProps = { ...otherProps };
  return inheritedProps;
});

onMounted(() => {
  dataValue.value =
    props.type === "number" &&
    typeof modelValue.value === "string" &&
    !Number.isNaN(Number.parseFloat(modelValue.value))
      ? Number.parseFloat(modelValue.value)
      : modelValue.value ?? null;
  nextTick(() => {
    if (!props.disabled && inputRef.value?.value !== "") {
      checkValidity();
    }
    if (
      attrs.required &&
      inputRef.value &&
      (typeof modelValue.value === "undefined" ||
        modelValue.value === "" ||
        inputRef.value.value === "undefined" ||
        inputRef.value.value === "")
    ) {
      inputRef.value.title = props.errors.get("valueMissing") ?? "";
    }
  });
});

const checkValidity = function (): boolean {
  errorKeys.value = [];
  if (inputRef.value) {
    inputRef.value.title = "";
  }

  if (props.disabled || props.readonly || !inputRef.value) {
    validity.value = true;
    return validity.value;
  }

  if (props.customValidation) {
    const validationResult = props.customValidation(computedValue.value);
    if (typeof validationResult === "boolean") {
      if (!validationResult && errorKeys.value.length === 0) {
        errorKeys.value.push("invalidValue");
        if (!props.errors.has("invalidValue")) {
          props.errors.set("invalidValue", "invalidValue");
        }
      }
      validity.value = validationResult;
      return validity.value;
    } else {
      console.warn("Custom validation function is invalid.");
    }
  }

  inputRef.value.checkValidity();

  if (
    inputRef.value.validity.valueMissing ||
    (attrs.required && (!inputRef.value.value || inputRef.value.value === ""))
  ) {
    errorKeys.value.push("valueMissing");
    if (!props.errors.has("valueMissing")) {
      props.errors.set("valueMissing", "valueMissing");
    }
    inputRef.value.title = getValidationMessage("valueMissing");
    validity.value = false;
    return validity.value;
  }

  if (inputRef.value.validity.badInput) {
    errorKeys.value.push("badInput");
    if (!props.errors.has("badInput")) {
      props.errors.set("badInput", "badInput");
    }
    inputRef.value.title = getValidationMessage("badInput");
    validity.value = false;
    return validity.value;
  }

  if (
    props.type === "email" &&
    (inputRef.value.validity.typeMismatch ||
      !validatePattern(
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
      ))
  ) {
    errorKeys.value.push("emailMismatch");
    if (!props.errors.has("emailMismatch")) {
      props.errors.set("emailMismatch", "emailMismatch");
    }
    inputRef.value.title = getValidationMessage("emailMismatch");
    validity.value = false;
  } else if (inputRef.value.validity.typeMismatch) {
    // Chose error message depending on type
    errorKeys.value.push(`${props.type}Mismatch`);
    if (!props.errors.has(`${props.type}Mismatch`)) {
      props.errors.set(`${props.type}Mismatch`, `${props.type}Mismatch`);
    }
    inputRef.value.title = getValidationMessage(`${props.type}Mismatch`);
    validity.value = false;
  }

  // Check if input value matches the provided pattern
  if (!validatePattern()) {
    // Chose error message depending on type
    if (props.type === "password") {
      errorKeys.value.push("passwordMismatch");
      if (!props.errors.has("passwordMismatch")) {
        props.errors.set("passwordMismatch", "passwordMismatch");
      }
      inputRef.value.title = getValidationMessage("passwordMismatch");
    } else {
      errorKeys.value.push("patternMismatch");
      if (!props.errors.has("patternMismatch")) {
        props.errors.set("patternMismatch", "patternMismatch");
      }
      inputRef.value.title = getValidationMessage("patternMismatch");
    }
    validity.value = false;
  }

  if (inputRef.value.validity.tooShort) {
    errorKeys.value.push("tooShort");
    if (!props.errors.has("tooShort")) {
      props.errors.set("tooShort", "tooShort");
    }
    inputRef.value.title = getValidationMessage("tooShort");
    validity.value = false;
  }

  if (inputRef.value.validity.tooLong) {
    errorKeys.value.push("tooLong");
    if (!props.errors.has("tooLong")) {
      props.errors.set("tooLong", "tooLong");
    }
    inputRef.value.title = getValidationMessage("tooLong");
    validity.value = false;
  }

  if (inputRef.value.validity.rangeUnderflow) {
    errorKeys.value.push("rangeUnderflow");
    if (!props.errors.has("rangeUnderflow")) {
      props.errors.set("rangeUnderflow", "rangeUnderflow");
    }
    inputRef.value.title = getValidationMessage("rangeUnderflow");
    validity.value = false;
  }

  if (inputRef.value.validity.rangeOverflow) {
    errorKeys.value.push("rangeOverflow");
    if (!props.errors.has("rangeOverflow")) {
      props.errors.set("rangeOverflow", "rangeOverflow");
    }
    inputRef.value.title = getValidationMessage("tooLong");
    validity.value = false;
  }

  if (!inputRef.value.validity) {
    errorKeys.value.push(inputRef.value.validationMessage);
    validity.value = false;
  } else if (!errorKeys.value.length) {
    inputRef.value.title = "";
    validity.value = true;
  }

  return validity.value;
};

function getValidationMessage(errorKey: string): string {
  return props.errors.get(errorKey) ?? errorKey;
}

function onBlur(): void {
  checkValidity();
  emit("blur", computedValue.value);
}

function onChange(event: Event): void {
  const value = (event.target as HTMLInputElement)?.value;
  computedValue.value = value;
  checkValidity();
  modelValue.value = computedValue.value;
  props.lazy ? emit("input", computedValue.value) : emit("change", computedValue.value);
}

function onInput(event: Event): void {
  if (!props.lazy) {
    const value = (event.target as HTMLInputElement)?.value;
    computedValue.value = value;
    checkValidity();
    modelValue.value = computedValue.value;
    emit("input", computedValue.value);
  }
}

function onKeypress(event: KeyboardEvent): void {
  if (props.type === "number") {
    const min =
      typeof attrs.min === "string" && !Number.isNaN(Number.parseFloat(attrs.min))
        ? Number.parseFloat(attrs.min)
        : null;
    if (event.key === "-") {
      if ((event.target as HTMLInputElement).value !== "" || (min !== null && min >= 0))
        event.preventDefault();
    } else if (event.key === ".") {
      if ((event.target as HTMLInputElement).value.includes(".")) event.preventDefault();
    } else if (Number.isNaN(Number.parseFloat(event.key))) {
      event.preventDefault();
    }
  }
}

const togglePasswordVisibility = function (): void {
  if (!props.disabled) {
    isPasswordVisible.value = !isPasswordVisible.value;
    dataType.value = isPasswordVisible.value ? "text" : "password";
  }

  nextTick(() => {
    inputRef.value?.focus();
  });
};

function validatePattern(pattern?: RegExp | string): boolean {
  if (pattern && inputRef.value) {
    return new RegExp(pattern).test(inputRef.value.value);
  }
  return inputRef.value &&
    attrs.pattern &&
    (typeof attrs.pattern === "string" || (attrs.pattern as any) instanceof RegExp)
    ? new RegExp(attrs.pattern as string | RegExp).test(inputRef.value.value)
    : true;
}

function onErrorsChanged(): void {
  if (inputRef.value && errorKeys.value.length) {
    inputRef.value.title = props.errors.get(errorKeys.value[0]) ?? "";
  }
}

function onDisabledOrReadonlyChanged(value: boolean): void {
  if (inputRef.value) {
    inputRef.value.title =
      value && errorKeys.value.length ? "" : props.errors.get(errorKeys.value[0]) ?? "";
  }
}

function onTypeChanged(type: InputType): void {
  dataType.value = type;
}

function onValueChanged(value: string | number | null | undefined): void {
  dataValue.value =
    dataType.value === "number" &&
    typeof value === "string" &&
    !Number.isNaN(Number.parseFloat(value))
      ? Number.parseFloat(value)
      : value;
}

watch(modelValue, onValueChanged);
watch(() => props.type, onTypeChanged);
watch(() => props.errors, onErrorsChanged, { deep: true });
watch(() => props.disabled, onDisabledOrReadonlyChanged);
watch(() => props.readonly, onDisabledOrReadonlyChanged);

defineExpose({
  modelValue,
  dataType,
  dataValue,
  errorKeys,
  isPasswordVisible,
  validity,
  inputRef,
  computedValue,
  checkValidity,
  getValidationMessage,
  togglePasswordVisibility,
  validatePattern,
});
</script>

<template>
  <label
    :data-pak="pak"
    class="bissantzControls inputControl"
    :class="[
      size ? `input-${size}` : '',
      {
        disabled: disabled,
        fullWidth: fullWidth,
        invalid: !validity,
        passwordToggle: passwordToggle,
        readonly: readonly,
      },
      ...rootClass,
    ]"
    :style="rootStyle"
    :data-design="design"
  >
    <span v-if="label" class="label">{{ label }}</span>
    <span v-if="placeholderWidth" class="widthtestbox">{{ $attrs.placeholder }}</span>
    <div class="inputContainer">
      <input
        ref="inputRef"
        v-bind="inheritedAttrs"
        :type="dataType"
        :value="computedValue"
        :disabled="disabled"
        :readonly="readonly"
        @input="onInput"
        @change="onChange"
        @blur="onBlur"
        @focus="emit('focus')"
        @keypress="onKeypress"
      />
      <template v-if="type === 'password' && passwordToggle">
        <template v-if="!isPasswordVisible">
          <span class="visibility" @click="togglePasswordVisibility">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="1.25em"
              height="1.5em"
            >
              <path
                d="M24 31.5q3.55 0 6.025-2.475Q32.5 26.55 32.5 23q0-3.55-2.475-6.025Q27.55 14.5 24 14.5q-3.55 0-6.025 2.475Q15.5 19.45 15.5 23q0 3.55 2.475 6.025Q20.45 31.5 24 31.5Zm0-2.9q-2.35 0-3.975-1.625T18.4 23q0-2.35 1.625-3.975T24 17.4q2.35 0 3.975 1.625T29.6 23q0 2.35-1.625 3.975T24 28.6Zm0 9.4q-7.3 0-13.2-4.15Q4.9 29.7 2 23q2.9-6.7 8.8-10.85Q16.7 8 24 8q7.3 0 13.2 4.15Q43.1 16.3 46 23q-2.9 6.7-8.8 10.85Q31.3 38 24 38Zm0-15Zm0 12q6.05 0 11.125-3.275T42.85 23q-2.65-5.45-7.725-8.725Q30.05 11 24 11t-11.125 3.275Q7.8 17.55 5.1 23q2.7 5.45 7.775 8.725Q17.95 35 24 35Z"
              />
            </svg>
          </span>
        </template>
        <template v-else>
          <span class="visibilityOff" @click="togglePasswordVisibility">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 48 48"
              width="1.25em"
              height="1.5em"
            >
              <path
                d="m31.45 27.05-2.2-2.2q1.3-3.55-1.35-5.9-2.65-2.35-5.75-1.2l-2.2-2.2q.85-.55 1.9-.8 1.05-.25 2.15-.25 3.55 0 6.025 2.475Q32.5 19.45 32.5 23q0 1.1-.275 2.175-.275 1.075-.775 1.875Zm6.45 6.45-2-2q2.45-1.8 4.275-4.025Q42 25.25 42.85 23q-2.5-5.55-7.5-8.775Q30.35 11 24.5 11q-2.1 0-4.3.4-2.2.4-3.45.95L14.45 10q1.75-.8 4.475-1.4Q21.65 8 24.25 8q7.15 0 13.075 4.075Q43.25 16.15 46 23q-1.3 3.2-3.35 5.85-2.05 2.65-4.75 4.65Zm2.9 11.3-8.4-8.25q-1.75.7-3.95 1.075T24 38q-7.3 0-13.25-4.075T2 23q1-2.6 2.775-5.075T9.1 13.2L2.8 6.9l2.1-2.15L42.75 42.6ZM11.15 15.3q-1.85 1.35-3.575 3.55Q5.85 21.05 5.1 23q2.55 5.55 7.675 8.775Q17.9 35 24.4 35q1.65 0 3.25-.2t2.4-.6l-3.2-3.2q-.55.25-1.35.375T24 31.5q-3.5 0-6-2.45T15.5 23q0-.75.125-1.5T16 20.15Zm15.25 7.1Zm-5.8 2.9Z"
              />
            </svg>
          </span>
        </template>
      </template>
    </div>
    <div
      v-show="!disabled && !readonly && !validity && errorKeys.length"
      class="validationMessage"
    >
      <span v-for="(errorKey, idx) in errorKeys" :key="idx" :data-locid="errorKey">{{
        getValidationMessage(errorKey)
      }}</span>
    </div>
  </label>
</template>

<style lang="less">
@import "../styles/components/bc-input.less";
</style>
